import React, { useState, useEffect } from "react";
import {
  Container,
  Modal,
  Button,
  Table,
  Card,
  Form,
  InputGroup,
  Badge,
  Alert,
} from "react-bootstrap";
import { FaSearch, FaCheck, FaBan, FaTrash } from "react-icons/fa";
import axios from "axios";
import { format } from "date-fns";

const Activations = () => {
  const [activations, setActivations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activationToDelete, setActivationToDelete] = useState(null);
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    // Fetch activations
    const fetchActivations = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await axios.get("/api/activations");
        setActivations(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching activations:", error);
        if (error.response && error.response.status === 401) {
          setError("Your session has expired. Please log in again.");
          // The axios interceptor will handle the logout
        } else {
          setError("Failed to fetch activations. Please try again later.");
        }
        setLoading(false);
      }
    };

    fetchActivations();
  }, []);

  const filteredActivations = activations.filter((activation) => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      (activation.license_id &&
        activation.license_id.toLowerCase().includes(searchLower)) ||
      (activation.user_id &&
        activation.user_id.toLowerCase().includes(searchLower)) ||
      (activation.user_name &&
        activation.user_name.toLowerCase().includes(searchLower)) ||
      (activation.machine_fingerprint &&
        activation.machine_fingerprint.toLowerCase().includes(searchLower)) ||
      (activation.ip_address &&
        activation.ip_address.toLowerCase().includes(searchLower))
    );
  });

  const formatFingerprint = (fingerprint) => {
    if (!fingerprint) {
      return "N/A";
    }
    return showFull ? fingerprint : `${fingerprint.slice(0, 8)}...`;
  };

  const getStatusBadge = (status) => {
    const now = new Date();

    if (status === "active") {
      return <Badge bg="success">Active</Badge>;
    } else if (status === "revoked") {
      return <Badge bg="danger">Revoked</Badge>;
    } else {
      return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleRevokeActivation = async (license_id) => {
    if (!window.confirm("Are you sure you want to revoke this activation?")) {
      return;
    }

    try {
      await axios.delete(`/api/activations/${license_id}`);
      // Update local state
      setActivations((prevActivations) =>
        prevActivations.map((activation) =>
          activation._id === license_id
            ? { ...activation, status: "revoked" }
            : activation
        )
      );
      console.log(`Activation ${license_id} successfully revoked`);
    } catch (error) {
      console.error("Error revoking activation:", error);
      alert("Failed to revoke activation. Please try again.");
    }
  };

  const handleRestoreActivation = async (license_id) => {
    if (!window.confirm("Are you sure you want to restore this activation?")) {
      return;
    }

    try {
      await axios.put(`/api/activations/${license_id}`, { status: "active" });

      // Update local state
      setActivations((prevActivations) =>
        prevActivations.map((activation) =>
          activation._id === license_id
            ? { ...activation, status: "active" }
            : activation
        )
      );
      console.log(`Activation ${license_id} successfully restored`);
    } catch (error) {
      console.error("Error restoring activation:", error);
      alert("Failed to restore activation. Please try again.");
    }
  };

  const handleDeleteActivation = (activation) => {
    setActivationToDelete(activation);
    setShowDeleteModal(true);
  };

  const confirmDeleteActivation = async () => {
    try {
      // Make a custom DELETE request with a special query parameter to indicate
      // permanent deletion instead of just revocation
      await axios.delete(
        `/api/activations/${activationToDelete._id}?permanent=true`
      );

      // Remove the license from the local state
      setActivations((prevActivations) =>
        prevActivations.filter(
          (activation) => activation._id !== activationToDelete._id
        )
      );

      setShowDeleteModal(false);
      console.log(
        `Activation ${activationToDelete.license_id} permanently deleted`
      );
    } catch (error) {
      console.error("Error deleting activation:", error);
      alert("Failed to delete activation. Please try again.");
    }
  };

  return (
    <Container>
      <h2 className="my-4">License Activations</h2>

      <Card className="mb-4">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search by license ID, user ID, user name, fingerprint, or IP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {loading ? (
            <p className="text-center">Loading activations...</p>
          ) : filteredActivations.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Activation ID</th>
                  <th>License Key</th>
                  <th>User</th>
                  <th>Machine Fingerprint</th>
                  <th>IP Address</th>
                  <th>Timestamp</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredActivations.map((activation, index) => (
                  <tr key={index}>
                    <td>
                      <code>{activation._id}</code>
                    </td>
                    <td>
                      <code>{activation.license_key}</code>
                    </td>
                    <td>{activation.user_name}</td>
                    <td
                      onClick={
                        activation.machine_fingerprint
                          ? () => setShowFull(!showFull)
                          : undefined
                      }
                      style={{
                        cursor: activation.machine_fingerprint
                          ? "pointer"
                          : "default",
                      }}
                      title={
                        activation.machine_fingerprint
                          ? "Click to toggle full view"
                          : "Fingerprint not available"
                      }
                    >
                      <code>
                        {formatFingerprint(activation.machine_fingerprint)}
                      </code>
                    </td>
                    <td>{activation.ip_address}</td>
                    <td>
                      {activation.timestamp &&
                        format(
                          new Date(activation.timestamp),
                          "MMM d, yyyy HH:mm:ss"
                        )}
                    </td>
                    <td>{getStatusBadge(activation.status)}</td>
                    <td>
                      {activation.status !== "revoked" ? (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="me-2"
                          title="Revoke Activation"
                          onClick={() => handleRevokeActivation(activation._id)}
                        >
                          <FaBan />
                        </Button>
                      ) : (
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          title="Restore Activation"
                          onClick={() =>
                            handleRestoreActivation(activation._id)
                          }
                        >
                          <FaCheck />
                        </Button>
                      )}

                      <Button
                        variant="outline-danger"
                        size="sm"
                        title="Delete Activation"
                        onClick={() => handleDeleteActivation(activation)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-center">No activations found.</p>
          )}
        </Card.Body>
      </Card>
      {/* Delete Activation Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Activation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activationToDelete && (
            <>
              <p>
                Are you sure you want to permanently delete this activation?
              </p>
              <p>
                <strong>This action cannot be undone.</strong>
              </p>
              <p>
                Activation ID: <code>{activationToDelete._id}</code>
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteActivation}>
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Activations;
